import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Modal,
  TextField,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/he"; // Hebrew locale support
import updateLocale from "dayjs/plugin/updateLocale";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import infoIcon from "../assets/images/info-icon.svg";
import grayInfoIcon from "../assets/images/gray_info.svg";
import closeIcon from "../assets/images/cross-close.svg";
import chrat from "../assets/images/chartimg.png";
import calenderIcon from "../assets/images/calendarlcon.svg";
import { useSearchParams } from "react-router-dom";
import identity__date_highlight from "../assets/images/identity__date_highlight.png";
import identity_highlight from "../assets/images/identity_highlight.png";
import { heIL } from "@mui/x-date-pickers/locales";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
];

dayjs.extend(updateLocale);
dayjs.updateLocale("he", {
  months: [
    "ינואר",
    "פברואר",
    "מרץ",
    "אפריל",
    "מאי",
    "יוני",
    "יולי",
    "אוגוסט",
    "ספטמבר",
    "אוקטובר",
    "נובמבר",
    "דצמבר",
  ],
  weekdays: ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"],
});

const UserDoc = () => {
  const [open, setOpen] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [idError, setIdError] = useState("");
  const [issueDate, setIssueDate] = useState(null);
  const [birthDate, setBirthDate] = useState(null);
  const [birthDateError, setBirthDateError] = useState("");
  const [modalContent, setModalContent] = useState({});
  const [dateModal, setDateModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { email, phoneNumber, fullName } = location.state || {};
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100% - 40px)",
    maxWidth: 467,
    // maxHeight: 324,
    bgcolor: "background.paper",
    borderRadius: "22px",
    boxShadow: 24,
    p: "10px 20px",
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email && !searchParams.get("skip")) {
      navigate("/register?step=1");
    }
  }, [email, navigate]);

  const handleOpen = (content) => {
    setModalContent(content);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setModalContent({});
  };

  const isAdult = (date) => dayjs().diff(date, "year") >= 18;
  const logApiCalls = (logs) => {
    axios.post(`${process.env.REACT_APP_BE_BASE_URL}api-log/save`, { logs }).catch(err => {
      console.error("Failed to save api logs:", err);
    });
  };

  const checkUserDetails = async () => {
    const state = location?.state;

    if (state?.idNumber && state?.birthDate && state?.issueDate) {
      const formattedBirthDate =
        typeof state.birthDate === "string"
          ? state.birthDate
          : dayjs(state.birthDate.$d || state.birthDate).format("DD/MM/YYYY");

      const formattedIssueDate =
        typeof state.issueDate === "string"
          ? state.issueDate
          : dayjs(state.issueDate.$d || state.issueDate).format("DD/MM/YYYY");

      // Salesforce requires YYYY-MM-DD date format
      const toSfDate = (ddmmyyyy) => {
        if (!ddmmyyyy) return null;
        const parts = ddmmyyyy.split("/");
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        return ddmmyyyy;
      };

      const getUtmData = () => {
        const data = {};
        UTM_KEYS.forEach((key) => {
          const value = localStorage.getItem(key) || "";
          if (value) data[key] = value;
        });
        return data;
      };

      const getLandingUrl = () => {
        const base = `${window.location.origin}/register?step=1`;
        const utmData = getUtmData();
        const params = new URLSearchParams(utmData).toString();
        return params ? `${base}&${params}` : base;
      };

      const commonPayload = {
        idNumber: state.idNumber,
        phone_number: phoneNumber,
        full_name: fullName,
        email,
        birthDate: formattedBirthDate,
        issueDate: formattedIssueDate,
        url: getLandingUrl(),
        ...getUtmData(),
      };

      try {
        const nameParts = (fullName || "").trim().split(/\s+/);
        const sfLeadPayload = {
          ProFin__First_Name__c: nameParts.slice(0, -1).join(" ") || nameParts[0] || "",
          ProFin__Last_Name__c: nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0] || "",
          ProFin__Email__c: email,
          ProFin__Mobile_Phone__c: phoneNumber,
          ProFin__National_ID_VAT_Number__c: state.idNumber,
          ProFin__Birth_Date__c: toSfDate(formattedBirthDate),
          ProFin__Issue_Date__c: toSfDate(formattedIssueDate),
          ProFin__Status__c: "Third Step Completed",
          ProFin__Source__c: "Web",
          Source_App__c: "Web App",
        };
        console.log("[MAKE_WEBHOOK] Sending payload (UserDoc):", commonPayload);
        // Run all 5 requests in parallel
        const [insuranceRes, pensionRes, salesforceRes, makeHookRes, sfLeadRes] =
          await Promise.allSettled([
            axios.post(
              "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/get_insurance",
              commonPayload,
              {
                headers: {
                  "X-API-Key":
                    "e0c43d33657138baebe70ec851cafd6786758280c7c79e2ff653c54a5b8ab32a",
                  Authorization:
                    "Basic cHJvZHVjdGl2ZUBwcm9maW5ibWMuY29tOkJNQzIwMjUhcHJvZmluIUBAQEBIVEE=",
                },
              },
            ),
            axios.post(
              "https://zco3myjcyc.execute-api.eu-central-1.amazonaws.com/get_pension",
              commonPayload,
            ),
            axios.post(
              "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/salesforce/clients",
              commonPayload,
              {
                headers: {
                  "X-API-Key":
                    "e0c43d33657138baebe70ec851cafd6786758280c7c79e2ff653c54a5b8ab32a",
                  Authorization:
                    "Basic cHJvZHVjdGl2ZUBwcm9maW5ibWMuY29tOkJNQzIwMjUhcHJvZmluIUBAQEBIVEE=",
                },
              },
            ),
            axios.post(
              "https://hook.eu1.make.com/hxk22yyj8th3neeg47m9vzqw60e8pi93",
              commonPayload,
            ),
            axios.post(
              `${process.env.REACT_APP_BE_BASE_URL}salesforce/lead`,
              sfLeadPayload,
            ),
          ]);

        // Log all parallel call results
        console.log("[MAKE_WEBHOOK]", makeHookRes.status === "fulfilled" ? "✅ Success:" : "❌ Failed:", makeHookRes.status === "fulfilled" ? makeHookRes.value?.status : makeHookRes.reason?.message);
        console.log("[SF_LEAD] (UserDoc)", sfLeadRes.status === "fulfilled" ? "✅ Success:" : "❌ Failed:", sfLeadRes.status === "fulfilled" ? sfLeadRes.value?.data : sfLeadRes.reason?.message);
        console.log("[GET_INSURANCE]", insuranceRes.status === "fulfilled" ? "✅ Success:" : "❌ Failed:", insuranceRes.status === "fulfilled" ? insuranceRes.value?.status : insuranceRes.reason?.message);
        console.log("[GET_PENSION]", pensionRes.status === "fulfilled" ? "✅ Success:" : "❌ Failed:", pensionRes.status === "fulfilled" ? pensionRes.value?.status : pensionRes.reason?.message);
        console.log("[SF_CLIENTS]", salesforceRes.status === "fulfilled" ? "✅ Success:" : "❌ Failed:", salesforceRes.status === "fulfilled" ? salesforceRes.value?.status : salesforceRes.reason?.message);

        // Construct logs array
        const logs = [
          {
            email,
            full_name: fullName,
            phone_number: phoneNumber,
            page: "UserDoc",
            registration_step: "step_2_to_3",
            api_name: "get_insurance",
            api_url: "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/get_insurance",
            was_called: true,
            skip_reason: null,
            request_payload: commonPayload,
            response_status: insuranceRes.status === "fulfilled" ? insuranceRes.value.status : (insuranceRes.reason?.status || null),
            response_settled: insuranceRes.status,
            response_data: insuranceRes.status === "fulfilled" ? insuranceRes.value.data : (insuranceRes.reason?.response?.data || null),
            error_message: insuranceRes.status === "rejected" ? insuranceRes.reason?.message : null
          },
          {
            email,
            full_name: fullName,
            phone_number: phoneNumber,
            page: "UserDoc",
            registration_step: "step_2_to_3",
            api_name: "get_pension",
            api_url: "https://zco3myjcyc.execute-api.eu-central-1.amazonaws.com/get_pension",
            was_called: true,
            skip_reason: null,
            request_payload: commonPayload,
            response_status: pensionRes.status === "fulfilled" ? pensionRes.value.status : (pensionRes.reason?.status || null),
            response_settled: pensionRes.status,
            response_data: pensionRes.status === "fulfilled" ? pensionRes.value.data : (pensionRes.reason?.response?.data || null),
            error_message: pensionRes.status === "rejected" ? pensionRes.reason?.message : null
          },
          {
            email,
            full_name: fullName,
            phone_number: phoneNumber,
            page: "UserDoc",
            registration_step: "step_2_to_3",
            api_name: "salesforce_clients",
            api_url: "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/salesforce/clients",
            was_called: true,
            skip_reason: null,
            request_payload: commonPayload,
            response_status: salesforceRes.status === "fulfilled" ? salesforceRes.value.status : (salesforceRes.reason?.status || null),
            response_settled: salesforceRes.status,
            response_data: salesforceRes.status === "fulfilled" ? salesforceRes.value.data : (salesforceRes.reason?.response?.data || null),
            error_message: salesforceRes.status === "rejected" ? salesforceRes.reason?.message : null
          },
          {
            email,
            full_name: fullName,
            phone_number: phoneNumber,
            page: "UserDoc",
            registration_step: "step_2_to_3",
            api_name: "make_webhook",
            api_url: "https://hook.eu1.make.com/hxk22yyj8th3neeg47m9vzqw60e8pi93",
            was_called: true,
            skip_reason: null,
            request_payload: commonPayload,
            url: commonPayload.url,
            utm_data: getUtmData(),
            response_status: makeHookRes.status === "fulfilled" ? makeHookRes.value.status : (makeHookRes.reason?.status || null),
            response_settled: makeHookRes.status,
            response_data: makeHookRes.status === "fulfilled" ? makeHookRes.value.data : (makeHookRes.reason?.response?.data || null),
            error_message: makeHookRes.status === "rejected" ? makeHookRes.reason?.message : null
          },
          {
            email,
            full_name: fullName,
            phone_number: phoneNumber,
            page: "UserDoc",
            registration_step: "step_2_to_3",
            api_name: "salesforce_lead_direct",
            api_url: `${process.env.REACT_APP_BE_BASE_URL}salesforce/lead`,
            was_called: true,
            skip_reason: null,
            request_payload: sfLeadPayload,
            response_status: sfLeadRes.status === "fulfilled" ? sfLeadRes.value.status : (sfLeadRes.reason?.status || null),
            response_settled: sfLeadRes.status,
            response_data: sfLeadRes.status === "fulfilled" ? sfLeadRes.value.data : (sfLeadRes.reason?.response?.data || null),
            error_message: sfLeadRes.status === "rejected" ? sfLeadRes.reason?.message : null
          }
        ];

        // Send logs to backend database
        logApiCalls(logs);

        // Check results
        const insuranceStatus =
          insuranceRes.status === "fulfilled" &&
          insuranceRes.value.status === 200;
        const pensionStatus =
          pensionRes.status === "fulfilled" && pensionRes.value.status === 200;
        const salesforceStatus =
          salesforceRes.status === "fulfilled" &&
          salesforceRes.value.status === 200;

        console.log("Insurance API Result:", insuranceRes);
        console.log("Pension API Result:", pensionRes);
        console.log("Salesforce AWS API Result:", salesforceRes);
        console.log("Salesforce Direct Lead API Result:", sfLeadRes);
        if (!insuranceStatus || !pensionStatus || !salesforceStatus) {
          Swal.fire({
            title: "Verification Failed",
            text: "Unable to verify or send data to all systems.",
            icon: "error",
            confirmButtonText: "OK",
          });
          navigate("/");
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "lead_form_submit",
            form_name: "register_step_3_lead_form",
            form_id: `register_step_3_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            page_url: window.location.href,
          });
          console.log("GTM lead_form_submit event fired for register step 3");

          navigate("/register?step=3", {
            state: {
              email,
              phoneNumber,
              fullName,
              idNumber: state.idNumber,
              birthDate: state.birthDate,
              issueDate: state.issueDate,
            },
          });
        }
      } catch (error) {
        console.error("Parallel verification failed:", error);
        Swal.fire({
          title: "System Error",
          text: "An unexpected error occurred during verification.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } else {
      // Missing required details so APIs won't be called
      logApiCalls([
        {
          email,
          full_name: fullName,
          phone_number: phoneNumber,
          page: "UserDoc",
          registration_step: "step_2_to_3",
          api_name: "get_insurance",
          api_url: "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/get_insurance",
          was_called: false,
          skip_reason: "missing_id_details",
          request_payload: null,
          response_status: null,
          response_settled: null,
          error_message: null
        },
        {
          email,
          full_name: fullName,
          phone_number: phoneNumber,
          page: "UserDoc",
          registration_step: "step_2_to_3",
          api_name: "get_pension",
          api_url: "https://zco3myjcyc.execute-api.eu-central-1.amazonaws.com/get_pension",
          was_called: false,
          skip_reason: "missing_id_details",
          request_payload: null,
          response_status: null,
          response_settled: null,
          error_message: null
        },
        {
          email,
          full_name: fullName,
          phone_number: phoneNumber,
          page: "UserDoc",
          registration_step: "step_2_to_3",
          api_name: "salesforce_clients",
          api_url: "https://f3fxrg0gd3.execute-api.eu-central-1.amazonaws.com/salesforce/clients",
          was_called: false,
          skip_reason: "missing_id_details",
          request_payload: null,
          response_status: null,
          response_settled: null,
          error_message: null
        },
        {
          email,
          full_name: fullName,
          phone_number: phoneNumber,
          page: "UserDoc",
          registration_step: "step_2_to_3",
          api_name: "make_webhook",
          api_url: "https://hook.eu1.make.com/hxk22yyj8th3neeg47m9vzqw60e8pi93",
          was_called: false,
          skip_reason: "missing_id_details",
          request_payload: null,
          response_status: null,
          response_settled: null,
          error_message: null
        },
        {
          email,
          full_name: fullName,
          phone_number: phoneNumber,
          page: "UserDoc",
          registration_step: "step_2_to_3",
          api_name: "salesforce_lead_direct",
          api_url: "https://objective.my.salesforce.com/services/data/v58.0/sobjects/Lead/",
          was_called: false,
          skip_reason: "missing_id_details",
          request_payload: null,
          response_status: null,
          response_settled: null,
          error_message: null
        }
      ]);
    }
  };

  const handleSubmitDetails = () => {
    let isValid = true;

    // ID Number Validation
    if (idNumber.length !== 9) {
      setIdError("תעודת זהות חייבת להכיל בדיוק 9 ספרות");
      isValid = false;
    } else {
      setIdError("");
    }

    // Birth Date Validation
    if (!birthDate || !isAdult(birthDate)) {
      setBirthDateError("עליך להיות לפחות בן 18");
      isValid = false;
    } else {
      setBirthDateError("");
    }

    if (!isValid) return;

    axios
      .post(`${process.env.REACT_APP_BE_BASE_URL}user/register?step=3`, {
        email: email || undefined,
        idNumber: idNumber || undefined,
        birthDate: birthDate ? birthDate.format("YYYY-MM-DD") : undefined,
        issueDate: issueDate ? issueDate.format("YYYY-MM-DD") : undefined,
      })
      .then(() => {
        toast.success("Details submitted successfully!");

        // Show popup with Continue button
        Swal.fire({
          title: "אימות פרטים",
          text: "לחץ על המשך כדי לאמת את פרטי הביטוח והפנסיה שלך.",
          icon: "info",
          iconColor: "#2e7870",
          showCancelButton: false,
          confirmButtonText: "המשך",
          confirmButtonColor: "#2e7870",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then((result) => {
          if (result.isConfirmed) {
            // Show loading popup after Continue is clicked
            Swal.fire({
              title: "אנא המתן",
              text: "מאמת את הפרטים שלך...",
              allowOutsideClick: false,
              allowEscapeKey: false,
              showConfirmButton: false,
              didOpen: () => {
                Swal.showLoading();
                // Change spinner color to #2e7870
                const loader = document.querySelector(".swal2-loader");
                if (loader) {
                  loader.style.borderColor =
                    "#2e7870 transparent #2e7870 transparent";
                }
              },
            });

            // Update location.state with current form values before calling API
            location.state = {
              ...location.state,
              email,
              phoneNumber,
              fullName,
              idNumber,
              birthDate: birthDate.format("DD/MM/YYYY"),
              issueDate: issueDate ? issueDate.format("DD/MM/YYYY") : null,
            };

            // Call checkUserDetails and close loader when done
            checkUserDetails().finally(() => {
              Swal.close();
            });
          }
        });
      })
      .catch(() => {
        toast.error("Unable to save details, please try again!");
      });
  };

  return (
    <div className="form_box">
      <h2 className="box_head mb_2" style={{ textAlign: "right" }}>
        פנייה לערוצי המידע
      </h2>
      <p className="box_head_p">
        מיד לאחר ההרשמה אנחנו נפנה לכל אחד מערוצי המידע הרלוונטים לקבלת המידע
        שלכם.{" "}
      </p>
      <p className="box_head_p">
        לאחר כ-72 שעות תהיה לנו תמונת מצב מלאה לגבי כל אחד מהמוצרים הפיננסים
        שלכם.
      </p>
      <p className="box_head_p">
        אנחנו ננתח את המידע בעזרת מערכת AI מתקדמת ונציג את התוצאות וההמלצות
        באזור האישי שלכם במגוון דאשבורים ודוחות מפורטים. את המידע תוכלו לקבל גם
        בתצורה של דוח למייל או לוואטסאפ.
      </p>
      <h5 className="box_head_h5">למי אנחנו פונים ? </h5>
      <span
        className="info_text"
        onClick={() =>
          handleOpen({
            title: "מסלקה פנסיונית",
            details:
              "מערכת המחוברת לכל חברות הביטוח, קרנות הפנסיה, קופות הגמל וקרנות ההשתלמות, שבאמצעותה יכולים חוסכים וחוסכות לפנסיה לקבל בקלות, תמונת מצב פנסיוני מלאה: באילו גופים ובאיזה חשבונות/ פוליסות יש לי כספים, סכום הכסף שנצבר בחסכונות השונים, הפקדות שוטפות, כיסויים למקרה מוות ונכות, תחזית לגיל קצבה ועוד.",
          })
        }
      >
        <img className="info_icon" src={infoIcon} alt="info" />
        מסלקה פנסיונית
      </span>
      <span
        className="info_text"
        onClick={() =>
          handleOpen({
            title: "הר הביטוח",
            details:
              'אתר של משרד האוצר המאגד בתוכו מידע על התיק הביטוחי שלך ומאפשר ליצור דו"ח המאגד את הביטוחים שלך בחברות הביטוח ותעריפיהם, מה שמאפשר לראות חוסרים, כפילויות ותעריפים גבוהים.',
          })
        }
      >
        <img className="info_icon" src={infoIcon} alt="info" />
        הר הביטוח
      </span>
      <span
        className="info_text"
        onClick={() =>
          handleOpen({
            title: "העתקי פוליסות ",
            details:
              "כמבוטח באחת מפוליסות הביטוח, באפשרותך לקבל העתק פוליסה. הפוליסה מכילה את הנתונים הבאים: דף פרטי הביטוח (נכון לביטוחים בתוקף בלבד וכולל פרטי מבוטחים בפוליסה), פרטי סוכן ודרכי יצירת קשר, פרטי התוכנית וסכומי תקרות התוכנית, פרמיות (עלות הביטוח למבוטח) ותנאים מיוחדים הקיימים בפוליסה.",
          })
        }
      >
        <img className="info_icon" src={infoIcon} alt="info" />
        חברות הביטוח - העתקי פוליסות
      </span>
      <div dir="rtl">
        {/* ID Number */}
        <label className="info_label">מספר תעודת זהות</label>
        <span
          onClick={() =>
            handleOpen({
              details: "",
              type: "no_title",
              title:
                "<span style={{fontWeight:'600'}}>כדי שנוכל לאסוף את נתוני הביטוח מאתר הר הביטוח אנחנו נדרשים לספק את הפרטים הבאים:<br>" +
                "מספר תעודת זהות, תאריך הנפקה ותאריך לידה.<br>" +
                "אין מה לדאוג, המידע מוצפן ומאובטח והוא לא ישמר במערכת בסיום התהליך.</span>",
            })
          }
          className="info_text mb_2"
        >
          למה אתם צריכים את מספר התז שלי?{" "}
        </span>

        <TextField
          value={idNumber}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, ""); // Only allow numbers
            if (value.length <= 9) {
              setIdNumber(value);
              setIdError("");
            }
          }}
          error={!!idError}
          helperText={
            idError ? (
              <FormHelperText sx={{ textAlign: "right" }} dir="rtl">
                {idError}
              </FormHelperText>
            ) : (
              ""
            )
          }
          size={window.innerWidth < 767 ? "small" : "medium"}
          InputProps={{ sx: { borderRadius: window.innerWidth > 767 ? 3 : 1 } }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#1b7870",
              },
            },
          }}
          placeholder="הזן את מספר תעודת הזהות שלך"
          variant="outlined"
        />

        {/* Issue Date */}
        <label className="info_label">תאריך הנפקה</label>
        <span className="">
          <div
            className="info_text"
            onClick={() =>
              handleOpen({
                details: "",
                type: "no_title",
                title:
                  "כדי שנוכל לאסוף את נתוני הביטוח מאתר הר הביטוח אנחנו נדרשים לספק את הפרטים הבאים: מספר תעודת זהות, תאריך הנפקה ותאריך לידה. אין מה לדאוג, המידע מוצפן ומאובטח והוא לא ישמר במערכת בסיום התהליך. ",
              })
            }
          >
            <img className="info_icon" src={infoIcon} alt="info" /> למה אתם
            צריכים את תאריך ההנפקה שלי ?
          </div>
          <div className="info_text" onClick={() => setDateModal(true)}>
            <img className="info_icon" src={infoIcon} alt="info" /> איפה אני
            מוצא את תאריך ההנפקה שלי ?
          </div>
          {/* <img className="info_icon" src={infoIcon} alt="info" /> */}
        </span>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="he"
          localeText={
            heIL.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <MobileDatePicker
            value={issueDate}
            onChange={(newValue) => setIssueDate(newValue)}
            sx={{ width: "100%" }}
            slotProps={{
              layout: {
                sx: {
                  ".MuiPickersLayout-contentWrapper": {
                    direction: "ltr",
                  },
                  ".MuiPickersCalendarHeader-root": {
                    direction: "ltr",
                  },
                  ".MuiPickersCalendarHeader-labelContainer": {
                    direction: "ltr",
                  },
                },
              },
              textField: {
                InputProps: {
                  sx: {
                    borderRadius: 3, // Border radius applied
                  },
                  startAdornment: (
                    <InputAdornment
                      sx={{ position: "absolute", left: "13px" }}
                      position="start"
                    >
                      <img
                        src={calenderIcon}
                        alt="calendar"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </InputAdornment>
                  ),
                  style: {
                    color: "black",
                  },
                },
                placeholder: "-- בחר תאריך הנפקה --",
              },
              field: {
                readOnly: false,
              },
            }}
          />
        </LocalizationProvider>

        {/* Birth Date */}
        <label className="info_label">תאריך לידה</label>
        <p>
          *חשוב במיוחד למלא תאריך לידה ו־תאריך הנפקה בצורה מדויקת 📅 אם אחד מהם
          לא תקין — המערכת לא תוכל למשוך נתונים מ־הר הביטוח ומ־המסלקה הפנסיונית
        </p>
        <span
          onClick={() =>
            handleOpen({
              details: "",
              type: "no_title",
              title:
                "<span style={{fontWeight:'600'}}>כדי שנוכל לאסוף את נתוני הביטוח מאתר הר הביטוח אנחנו נדרשים לספק את הפרטים הבאים:<br>" +
                "מספר תעודת זהות, תאריך הנפקה ותאריך לידה.<br>" +
                "אין מה לדאוג, המידע מוצפן ומאובטח והוא לא ישמר במערכת בסיום התהליך.</span>",
            })
          }
          className="mb_2 info_text"
        >
          <img className="info_icon" src={infoIcon} alt="info" /> למה אתם צריכים
          את תאריך הלידה שלי ?
        </span>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="he"
          localeText={
            heIL.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <MobileDatePicker
            value={birthDate}
            onChange={(newValue) => {
              if (newValue && isAdult(newValue)) {
                setBirthDate(newValue);
                setBirthDateError("");
              } else {
                setBirthDateError("עליך להיות לפחות בן 18");
              }
            }}
            sx={{ width: "100%" }}
            maxDate={dayjs()} // Prevents future dates
            slotProps={{
              layout: {
                sx: {
                  ".MuiPickersLayout-contentWrapper": {
                    direction: "ltr",
                  },
                  ".MuiPickersCalendarHeader-root": {
                    direction: "ltr",
                  },
                  ".MuiPickersCalendarHeader-labelContainer": {
                    direction: "ltr",
                  },
                },
              },
              textField: {
                error: !!birthDateError,
                helperText: birthDateError ? (
                  <FormHelperText sx={{ textAlign: "right" }} dir="rtl">
                    {birthDateError}
                  </FormHelperText>
                ) : (
                  ""
                ),
                InputProps: {
                  sx: {
                    borderRadius: 3,
                  },
                  startAdornment: (
                    <InputAdornment
                      sx={{ position: "absolute", left: "13px" }}
                      position="start"
                    >
                      <img
                        src={calenderIcon}
                        alt="calendar"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </InputAdornment>
                  ),
                  style: {
                    color: "black",
                  },
                },
                placeholder: "-- בחר תאריך לידה --",
              },
              field: {
                readOnly: false, // This allows manual typing
              },
            }}
          />
        </LocalizationProvider>

        {/* {birthDateError && <FormHelperText error>{birthDateError}</FormHelperText>} */}
      </div>

      <button className="login_btn" onClick={handleSubmitDetails}>
        קחו אותי לשלב הבא
      </button>
      {/* <Link to="/register?step=3&skip=true" className="quit_link">לדלג</Link> */}
      {/* <div
        className="text-center"
        onClick={() => handleOpen({ type: "confirmation_modal" })}
        // onClick={() => navigate('/register?step=3&skip=true', { state: { email } })}
      >
        <span className="link_btn">אני רוצה לדלג על שלב זה </span>
      </div> */}

      {/* Info Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...modalStyle }}>
          <div className="m-close" onClick={handleClose}>
            <img src={closeIcon} alt="close" />
          </div>
          {/* <div className="m-image">
            <img src={chrat} alt="chart" />
          </div> */}
          {modalContent?.type === "confirmation_modal" ? (
            <div>
              <span className="m-info_label d-block">
                <h3 className="text-center w_100">שים לב !</h3>
              </span>
              <p className="modal-details text-center">
                הפרטים נדרשים על מנת לאסוף את כל המידע הביטוחי והפיננסי מהמערכות
                השונות.
                <br />
                במידה ותדלג/י על שלב זה לא נוכל להציג מידע מלא עבורך.
                <br />
                <strong className="info_text">
                  {" "}
                  למה אתם צריכים את המידע הזה ?{" "}
                </strong>{" "}
              </p>
              <button className="login_btn" onClick={() => handleClose()}>
                חזור למסך הפרטים{" "}
              </button>
              <div
                className="text-center mb_2"
                onClick={() =>
                  navigate("/register?step=3&skip=true", {
                    state: { email, phoneNumber, fullName },
                  })
                }
              >
                <span className="link_btn">אני רוצה לדלג על שלב זה </span>
              </div>
            </div>
          ) : (
            <div>
              <span className="m-info_label">
                <img src={grayInfoIcon} alt="info" />
                {modalContent?.type !== "no_title" ? (
                  <strong>{modalContent?.title}</strong>
                ) : (
                  <span
                    dangerouslySetInnerHTML={{ __html: modalContent?.title }}
                  ></span>
                )}
              </span>
              <p className="modal-details">{modalContent?.details}</p>
            </div>
          )}
        </Box>
      </Modal>

      <Modal open={dateModal} onClose={() => setDateModal(false)}>
        <Box sx={modalStyle}>
          <div className="m-close" onClick={() => setDateModal(false)}>
            <img src={closeIcon} alt="close" />
          </div>

          <div>
            <div className="m-image">
              <img src={identity_highlight} alt="chart" />
            </div>
            <p className="m-info_label">
              <img src={infoIcon} alt="info" />
              מיקום תאריך הנפקה בתעודה ביומטרית{" "}
            </p>
          </div>
          <div>
            <div className="m-image">
              <img src={identity__date_highlight} alt="chart" />
            </div>
            <p className="m-info_label">
              <img src={infoIcon} alt="info" />
              מיקום תאריך הנפקה בתעודה רגילה{" "}
            </p>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default UserDoc;
